import { useQuery, useLazyQuery } from "@apollo/client"
import React, { useEffect, useState } from "react"
import { useParams } from "react-router"
import styled from "styled-components"
import Loader from "../../../components/Loader"
import Table, { Row } from "../../../components/Table"
import { GET_PERSON_DOCUMENTS } from "./query"
import { GET_SEDICII_VERIFICATIONS_BY_PERSON_ID } from "./sediciiQuery"
import {
  getPersonDocuments,
  GetSediciiVerificationsByPersonIdResponse,
  GetSediciiVerificationsByPersonIdVariables,
  SediciiVerification,
  SediciiDocumentType,
} from "../../../@types/graphql"
import { formatTag, formatTimeDate } from "../../../helpers/formatters"
import useFeatureConfig from "../../../hooks/useFeatureConfig"

const Wrapper = styled.div`
  max-width: 1080px;
  margin: 30px auto;
`

export const headings = ["Document", "Type", "Date created", "Source"]

function getDocumentTypeFromTags(tags: string[]): string {
  return tags
    .filter(
      (t) => t !== "no-upload" && t !== "uploaded" && !t.startsWith("date:")
    )
    .map(formatTag)
    .join(", ")
}

function formatSediciiDocumentType(docType: SediciiDocumentType): string {
  return docType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

interface MappedDocument {
  id: string
  filename: string | null
  contentType: string
  tags: string[]
  url: string
  createdAt: string
  createdBy: string
  __typename: "Document"
}

function mapSediciiDocumentsToTableFormat(
  verifications: SediciiVerification[]
): MappedDocument[] {
  const documents: MappedDocument[] = []

  verifications.forEach((verification) => {
    verification.documents.forEach((doc) => {
      documents.push({
        id: doc.id,
        filename: doc.fileName,
        contentType: doc.contentType || "application/octet-stream",
        tags: [
          "sedicii",
          formatSediciiDocumentType(doc.documentType),
          `status:${verification.status}`,
        ],
        url: doc.downloadUrl,
        createdAt: doc.createdAt,
        createdBy: "Sedicii",
        __typename: "Document",
      })
    })
  })

  return documents
}

const Documents = () => {
  const { id } = useParams<{ id: string }>()
  const [tagAsDocumentFilename] = useFeatureConfig(["TagAsDocumentFilename"])
  const [sediciiQueryTriggered, setSediciiQueryTriggered] = useState(false)

  const { loading, error, data } = useQuery<getPersonDocuments>(
    GET_PERSON_DOCUMENTS,
    {
      variables: { id },
    }
  )

  const [
    fetchSediciiVerifications,
    { loading: sediciiLoading, error: sediciiError, data: sediciiData },
  ] = useLazyQuery<
    GetSediciiVerificationsByPersonIdResponse,
    GetSediciiVerificationsByPersonIdVariables
  >(GET_SEDICII_VERIFICATIONS_BY_PERSON_ID)

  // TODO: Replace this placeholder condition with actual business logic
  const shouldFetchSedicii: boolean = true

  useEffect(() => {
    if (shouldFetchSedicii && id && !sediciiQueryTriggered) {
      setSediciiQueryTriggered(true)
      fetchSediciiVerifications({
        variables: {
          input: { personId: id },
        },
      })
    }
  }, [shouldFetchSedicii, id, sediciiQueryTriggered, fetchSediciiVerifications])

  if (loading || (shouldFetchSedicii && sediciiLoading)) {
    return <Loader />
  }

  if (error) {
    return <div>Error</div>
  }

  if (sediciiError) {
    return <div>Error loading Sedicii verifications</div>
  }

  if (!data) {
    return <div>User not found</div>
  }

  if (data.node.__typename !== "Person") {
    return <div>User not found</div>
  }

  //   Product Change Document
  // This is a once off change to manually display this document in the middle office
  const person = data?.node

  const accountStatus =
    person?.accounts?.items[0]?.account?.backOfficeAccount?.status.toString()

  const isOldUser = new Date(person.createdAt) < new Date("2022-02-13")

  const [...documentData] = data?.node?.documents?.items

  if (isOldUser && (accountStatus === "Open" || accountStatus === "Pending")) {
    documentData.push({
      contentType: "PDF",
      createdAt: "2022-02-14T13:00:48.355Z",
      createdBy: "person-0vuu96mZYCe",
      filename: null,
      id: "doc-0123",
      tags: ["no-upload", "Product Change Notification"],
      url: "https://smartinvest.irishlife.ie/pdfs/Product-Change-Notification.pdf",
      __typename: "Document",
    })
  }

  // Merge Sedicii documents if available
  if (sediciiData?.getSediciiVerificationsByPersonId?.length) {
    const sediciiDocuments = mapSediciiDocumentsToTableFormat(
      sediciiData.getSediciiVerificationsByPersonId
    )
    documentData.push(...sediciiDocuments)
  }

  const sortedData = documentData
    ?.slice()
    .sort((a, b) =>
      a.createdAt > b.createdAt ? -1 : b.createdAt > a.createdAt ? 1 : 0
    )

  //  End Product Change Document

  const rows = sortedData.map(
    ({ id: docId, filename, tags, contentType, createdAt, url, createdBy }) => {
      const docSource = createdBy === data.node.id ? "User" : "System"
      const document = tagAsDocumentFilename
        ? getDocumentTypeFromTags(tags)
        : filename
      const formattedDocType = contentType
        .substring(contentType.indexOf("/") + 1)
        .toUpperCase()

      return (
        <Row
          key={docId}
          values={[
            document || "Unknown",
            formattedDocType,
            formatTimeDate(createdAt),
            docSource,
          ]}
          onClick={() => {
            if (url) {
              window.open(url, "_blank")
            }
          }}
        />
      )
    }
  )

  return (
    <Wrapper data-testid="documents">
      <Table columnNames={headings} isEmpty={rows.length === 0}>
        {rows}
      </Table>
    </Wrapper>
  )
}

export default Documents
